import { useState, useEffect, useCallback, useRef } from 'react'
import { useOutletContext } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import { useTranslation } from 'react-i18next'

interface OutletContext {
  currentStep: number
  goToStep: (index: number) => void
  handleForward: () => void
  handleBack: () => void
}

interface NominatimResult {
  display_name: string
  lat: string
  lon: string
}

export default function LocationScreen() {
  const { t, i18n } = useTranslation()
  const { handleForward } = useOutletContext<OutletContext>()
  const { user } = useAuth()
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([])
  const [selected, setSelected] = useState<NominatimResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [visible, setVisible] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Geocoding feedback: 'not_found' (no matches) or 'network' (fetch failed)
  const [searchError, setSearchError] = useState<'not_found' | 'network' | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  // Nominatim geocoding with debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSearchError(null) // new input clears any previous geocoding message

    if (!query.trim() || query.length < 3) {
      setSuggestions([])
      return
    }

    setLoading(true)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
          { headers: { 'Accept-Language': i18n.language } }
        )
        if (!res.ok) throw new Error('Geocoding failed')
        const data: NominatimResult[] = await res.json()
        setSuggestions(data)
        if (data.length === 0) setSearchError('not_found')
      } catch {
        setSuggestions([])
        setSearchError('network')
      } finally {
        setLoading(false)
      }
    }, 400)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, i18n.language])

  const handleSelect = useCallback((result: NominatimResult) => {
    setSelected(result)
    setQuery(result.display_name)
    setSuggestions([])
    setError(null)
    setSearchError(null)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!selected || !user) return
    setSaving(true)
    setError(null)

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          location_text: selected.display_name,
          location_point: `POINT(${selected.lon} ${selected.lat})`,
        })
        .eq('id', user.id)

      if (updateError) throw updateError
      handleForward()
    } catch (err: unknown) {
      console.error('Onboarding location save failed:', err)
      const message = err instanceof Error ? err.message
        : (err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : t('onboarding.save_failed'))
      setError(message)
    } finally {
      setSaving(false)
    }
  }, [selected, user, handleForward])

  const handleSkip = useCallback(() => {
    handleForward()
  }, [handleForward])

  return (
    <div className="flex flex-col py-8">
      {/* Title */}
      <h1
        className={`font-display text-3xl text-ink mb-2 transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.location_title')}
      </h1>

      {/* Subtitle */}
      <p
        className={`font-body text-ink-muted leading-relaxed mb-8 transition-all duration-700 delay-100 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        {t('onboarding.location_subtitle')}
      </p>

      {/* Location input — high stacking context so its absolute dropdown
          overlays (and stays clickable above) everything below it */}
      <div
        className={`relative z-30 transition-all duration-700 delay-200 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
      >
        <Input
          type="text"
          placeholder={t('onboarding.location_placeholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            if (selected && e.target.value !== selected.display_name) {
              setSelected(null)
            }
          }}
          className="w-full h-12 text-base pr-10"
          autoComplete="off"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-green border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Suggestions dropdown */}
        {suggestions.length > 0 && !selected && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-parchment border border-ink/10 rounded-warm shadow-lg overflow-hidden">
            {suggestions.map((s, i) => (
              <button
                key={`${s.lat}-${s.lon}-${i}`}
                onClick={() => handleSelect(s)}
                className="w-full text-left px-4 py-3 font-body text-sm text-ink hover:bg-green/5 transition-colors border-b border-ink/5 last:border-0"
                type="button"
              >
                {s.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Geocoding feedback — shown when the search finds nothing or fails;
          clears itself as soon as the user types again */}
      {searchError && !selected && (
        <p className="mt-3 text-ink-muted text-sm font-body transition-all duration-500">
          {searchError === 'not_found'
            ? t('onboarding.location_not_found')
            : t('onboarding.location_error')}
        </p>
      )}

      {/* Selected confirmation */}
      {selected && (
        <div
          className={`mt-4 p-4 bg-green/5 rounded-warm border border-green/10 transition-all duration-500`}
        >
          <p className="font-body text-sm text-green font-medium">
            {t('onboarding.location_selected')}
          </p>
          <p className="font-body text-sm text-ink mt-1">
            {selected.display_name}
          </p>
        </div>
      )}

      {error && (
        <p className="mt-4 text-terra text-sm font-body">{error}</p>
      )}

      {/* Confirm button — no stacking context of its own, so the input wrapper (z-30)
          and its absolute dropdown (z-50) always overlay it and stay clickable */}
      <div className="mt-8 space-y-3">
        <Button
          onClick={handleConfirm}
          disabled={!selected || saving}
          className="w-full h-12 btn-primary"
        >
          {saving ? t('common.saving') : t('onboarding.next')}
        </Button>

        <Button
          variant="ghost"
          onClick={handleSkip}
          className="w-full h-12 text-ink-muted hover:text-ink font-body"
        >
          {t('onboarding.location_skip')}
        </Button>
      </div>
    </div>
  )
}
